import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { IoArrowBack, IoLink, IoInformationCircle, IoChatbubble, IoLogoApple, IoMailOutline } from 'react-icons/io5';
import { FcGoogle } from 'react-icons/fc';
import { signOut } from 'firebase/auth';
import { auth } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';
import { linkSocialToPhone } from '../../../services/authService';
import AlertModal from '../../../components/common/AlertModal';
import { isRealtorAutoConnectRedirect, normalizeSignupRedirectUrl } from '../../../utils/signupRedirect';

const PROVIDER_META = {
  kakao: { label: '카카오 계정', bg: '#FEE500' },
  google: { label: '구글 계정', bg: '#FFFFFF', border: '#E5E7EB' },
  apple: { label: '애플 계정', bg: '#000000', color: '#fff' },
  email: { label: '이메일 계정', bg: '#EFF4FF' },
};

const ProviderIcon = ({ provider, size = 20 }) => {
  if (provider === 'kakao') return <IoChatbubble size={size} color="#000" />;
  if (provider === 'apple') return <IoLogoApple size={size} color="#fff" />;
  if (provider === 'google') return <FcGoogle size={size} />;
  return <IoMailOutline size={size} color="#4A6CF7" />;
};

const maskPhone = (phone) => {
  const d = (phone || '').replace(/\D/g, '');
  if (d.length !== 11) return phone;
  return `${d.slice(0, 3)}-****-${d.slice(7)}`;
};

const MergeConfirmPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshSession } = useAuth();
  const state = location.state || {};
  const { authUid, provider, phone, verifiedToken, existingUser, returnTo } = state;
  const safeReturnTo = normalizeSignupRedirectUrl(returnTo);

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!authUid || !provider || !existingUser || !verifiedToken) {
      navigate('/login', { replace: true });
    }
  }, [authUid, provider, existingUser, verifiedToken, navigate]);

  if (!authUid || !provider || !existingUser) return null;

  const existingProvider = existingUser.linkedSocialProvider || 'email';

  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await linkSocialToPhone({
        phone,
        verifiedToken,
        socialUid: authUid,
        provider,
      });
      // 링크 완료 — 기존 계정의 상태에 따라 분기
      if (existingUser.status === 'pending') {
        await signOut(auth);
        setAlert({
          type: 'info',
          title: '이음에서 승인을 검토 중입니다',
          message: '관리자 승인 후 로그인하실 수 있습니다.',
          onConfirm: () => navigate('/login', { replace: true }),
        });
        return;
      }
      if (existingUser.status === 'suspended') {
        await signOut(auth);
        setAlert({
          type: 'error',
          title: '계정이 정지되었습니다',
          message: '관리자에게 문의하세요.',
          onConfirm: () => navigate('/login', { replace: true }),
        });
        return;
      }
      await refreshSession();
      const canReturnToRealtorAuto =
        isRealtorAutoConnectRedirect(safeReturnTo) && ['admin', 'staff', 'realtor'].includes(existingUser.role);
      if (safeReturnTo && (existingUser.role === 'realtor' || canReturnToRealtorAuto)) {
        window.location.assign(safeReturnTo);
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      setAlert({
        type: 'error',
        title: '통합에 실패했습니다',
        message: err?.message || '잠시 후 다시 시도해 주세요.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try { await signOut(auth); } catch {}
    navigate('/login', { replace: true });
  };

  return (
    <Wrapper>
      <Card>
        <BackBtn onClick={() => navigate(-1)}>
          <IoArrowBack size={16} />
          <span>뒤로가기</span>
        </BackBtn>

        <IconCircle>
          <IoLink size={22} color="#4A6CF7" />
        </IconCircle>

        <Title>기존 계정이 발견되었습니다</Title>
        <SubTitle>
          동일한 휴대폰 번호 <strong>{maskPhone(phone)}</strong>으로 가입된 계정이 있습니다.
        </SubTitle>

        <AccountCard>
          <SmallLabel>기존 가입 계정</SmallLabel>
          <AccountRow>
            <Avatar $provider={existingProvider}>
              <ProviderIcon provider={existingProvider} />
            </Avatar>
            <AccountBody>
              <AccountName>{PROVIDER_META[existingProvider]?.label || '이음 계정'}</AccountName>
              <AccountDesc>이미 가입된 소셜 계정</AccountDesc>
            </AccountBody>
            <Badge $variant="gray">기존 계정</Badge>
          </AccountRow>
        </AccountCard>

        <AccountCard>
          <SmallLabel>새로 로그인 시도한 계정</SmallLabel>
          <AccountRow>
            <Avatar $provider={provider}>
              <ProviderIcon provider={provider} />
            </Avatar>
            <AccountBody>
              <AccountName>{PROVIDER_META[provider]?.label || '소셜 계정'}</AccountName>
              <AccountDesc>통합 요청 계정</AccountDesc>
            </AccountBody>
            <Badge $variant="accent">신규 연결</Badge>
          </AccountRow>
        </AccountCard>

        <InfoBox>
          <InfoHeader>
            <IoInformationCircle size={18} color="#4A6CF7" />
            <span>계정 통합 안내</span>
          </InfoHeader>
          <InfoList>
            <li>두 소셜 계정이 하나의 이음 계정으로 통합됩니다.</li>
            <li>통합 후 두 소셜 계정 모두로 로그인할 수 있습니다.</li>
            <li>기존 계정의 모든 데이터는 그대로 유지됩니다.</li>
            <li>1인 1계정 원칙에 따라 통합을 취소할 수 없습니다.</li>
          </InfoList>
        </InfoBox>

        <PrimaryBtn type="button" disabled={loading} onClick={handleConfirm}>
          <IoLink size={18} />
          <span>{loading ? '통합 중...' : '계정 통합하기'}</span>
        </PrimaryBtn>

        <SecondaryBtn type="button" onClick={handleCancel}>
          취소하고 로그인으로 돌아가기
        </SecondaryBtn>
      </Card>

      {alert && (
        <AlertModal
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onConfirm={() => {
            const cb = alert.onConfirm;
            setAlert(null);
            cb?.();
          }}
        />
      )}
    </Wrapper>
  );
};

export default MergeConfirmPage;

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.bg};

  @media (min-width: 1024px) {
    align-items: center;
    background: #2f3542;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xl};

  @media (min-width: 1024px) {
    padding: 40px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  }
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #EFF4FF;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const SubTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};

  strong { color: ${({ theme }) => theme.colors.text}; font-weight: ${({ theme }) => theme.fontWeight.semibold}; }
`;

const AccountCard = styled.div`
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: #fff;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SmallLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const AccountRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $provider }) => PROVIDER_META[$provider]?.bg || '#EFF4FF'};
  border: 1px solid ${({ $provider }) => PROVIDER_META[$provider]?.border || 'transparent'};
`;

const AccountBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const AccountName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const AccountDesc = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Badge = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  padding: 4px 8px;
  border-radius: 6px;
  ${({ $variant, theme }) =>
    $variant === 'accent'
      ? `background: ${theme.colors.warning}22; color: ${theme.colors.warning};`
      : `background: ${theme.colors.borderLight}; color: ${theme.colors.textSecondary};`}
`;

const InfoBox = styled.div`
  padding: 14px 16px;
  background: #EFF4FF;
  border-radius: ${({ theme }) => theme.radius.md};
  margin: ${({ theme }) => theme.spacing.base} 0 ${({ theme }) => theme.spacing.lg};
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const InfoList = styled.ul`
  margin: 0;
  padding-left: 20px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #4A6CF7;
  line-height: 1.7;
`;

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SecondaryBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: #fff;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;
